import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Icon } from "leaflet";
import { TileLayer } from 'react-leaflet';
import { Icon as AntIcon, Tooltip as AntTooltip } from 'antd';
import "leaflet/dist/leaflet.css";
import moment from "moment";

import "./style.css";
import axios from '../../util/Api'
import { fetchAssetDeviceLastDataBulk } from '../../appRedux/actions/Asset'
import MapGroup from '../../components/Map/MapGroup';
import BaseMap from '../../components/Map/BaseMap';
import { CATEGORY_COLORS, CATEGORY_ICONS, CATEGORY_LABELS } from '../../util/drawingUtils';
import { TILE_LAYERS, getLeafletTileConfig, getTileLayerAttribution } from '../../util/mapTiles';

// true: load last data with the bulk API (50 devices per call, 3 calls at a time).
// false: the original one-request-per-device loop below. Flip to false to roll back.
const USE_BULK_LAST_DATA = true
const BULK_LAST_DATA_CHUNK_SIZE = 50
const BULK_LAST_DATA_PARALLEL_CHUNKS = 3

function DeviceMap(props) {
    const [currentLocation, setCurrentLocation] = useState(null);
    const [map, setMap] = useState(false);
    const [deviceData, setDeviceData] = useState({})
    const [deviceMaps, setDeviceMaps] = useState(new Map())
    const [processedDeviceData, setProcessedDeviceData] = useState({})
    const [devicesMarkerPoint, setDevicesMarkerPoint] = useState([])
    const [finishedCallingLastData, setFinishedCallingLastData] = useState(0);
    const [assetDeviceLastData, setAssetDeviceLastData] = useState({})
    const [assetDeviceLastDataLoading, setAssetDeviceLastDataLoading] = useState(false);
    const [pendingRequests, setPendingRequests] = useState(0);
    const [currentZoom, setCurrentZoom] = useState(5);
    const [scrollWheelZoom, setScrollWheelZoom] = useState(false);
    // POI (geo-fence) overlay controls
    const [hiddenPoiCategories, setHiddenPoiCategories] = useState([])
    const [poiPanelOpen, setPoiPanelOpen] = useState(false)
    const [loadedPoiCategories, setLoadedPoiCategories] = useState([])
    // Tile layer (default / satellite) switcher
    const [tileLayerType, setTileLayerType] = useState('current')
    // Full-screen (maximize) toggle — scoped to this map only
    const [isMaximized, setIsMaximized] = useState(false)
    const [isDarkMode, setIsDarkMode] = useState(
        typeof document !== 'undefined' && document.body
            ? document.body.classList.contains('dark-theme')
            : false
    )
    const mapRef = useRef();

    const handleZoomChange = () => {
        if (mapRef.current && mapRef.current.leafletElement) {
            const zoom = mapRef.current.leafletElement.getZoom();
            setCurrentZoom(zoom);
        }
    };

    // POI overlay resolves fences for the org owning these assets (falls back
    // to the user's visible fences when no organisation is available).
    const organisationId = useMemo(() => {
        const org = (props.assets || []).map(a => a && a.organisation).find(Boolean)
        if (!org) return null
        if (typeof org === 'string') return org
        return org._id || org.id || null
    }, [props.assets])

    // Keep isDarkMode in sync with body class changes
    useEffect(() => {
        if (typeof document === 'undefined' || !document.body) return
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.body.classList.contains('dark-theme'))
        })
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    // Resolved tile URL / subdomains — react to both tile type and dark-mode switches
    const resolvedTileUrl = useMemo(() => {
        if (tileLayerType !== 'current') return TILE_LAYERS[tileLayerType].url || undefined
        return getLeafletTileConfig(isDarkMode).url || undefined
    }, [tileLayerType, isDarkMode])

    const resolvedTileSubdomains = useMemo(() => {
        if (tileLayerType !== 'current') return TILE_LAYERS[tileLayerType].subdomains || undefined
        return getLeafletTileConfig(isDarkMode).subdomains || undefined
    }, [tileLayerType, isDarkMode])

    // Recalculate tile coverage after the container resizes (maximize / restore)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (mapRef.current && mapRef.current.leafletElement) {
                mapRef.current.leafletElement.invalidateSize();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [isMaximized])

    const togglePoiCategory = (cat) => {
        setHiddenPoiCategories((prev) =>
            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
        )
    };

    const [api, setApi] = useState(process.env.REACT_APP_ENVIRONMENT === `development`
        ? `http://localhost:3002/v2/`
        : process.env.REACT_APP_ENVIRONMENT === `production`
            ? `https://api.theassetpro.com/v2/`
            : `https://api.quonitrix.com/v2/`,)

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.leafletElement.locate();
        }
    }, [mapRef]);

    useEffect(() => {
        // Simulate getting new marker location, you can replace this with actual logic
        if (deviceData && Object.entries(deviceData).length && mapRef.current && mapRef.current.leafletElement) {
            // console.log(Object.entries(deviceData)[0][1]['lat_long'])
            // const newLoc = Object.entries(deviceData)[0][1]['lat_long'];
            // Use the first device that has a valid location, not always the first entry: if the first
            // device has no data (''), the map was never enabled (setMap) and no markers were drawn.
            const firstWithLocation = Object.values(deviceData).find(d => d && d.lat_long && d.lat_long.split(',').length === 2);
            const newLoc = firstWithLocation && firstWithLocation.lat_long;
            if (newLoc && newLoc.split(',').length === 2) {
                const newMarkerLocation = [newLoc.split(',')[0], newLoc.split(',')[1]]; // New marker location
                setCurrentLocation(newMarkerLocation);
                setTimeout(() => {
                    // setMarkerLocation(newMarkerLocation);
                    if (mapRef.current && mapRef.current.leafletElement)
                        mapRef.current.leafletElement.flyTo(newMarkerLocation, 4);
                    setMap(true)
                }, 1000); // Simulate after 5 seconds
            }
        }
    }, [deviceData, mapRef]);

    useEffect(() => {
        if (deviceData && Object.entries(deviceData).length) {
            // console.log('deviceData=>', deviceData);
            const processedData = {};
            Object.entries(deviceData).forEach(([key, doc]) => {
                // console.log('doc=>' + key, doc);
                const {
                    speed_gnss,
                    speed_oem,
                    diag_trouble_code,
                    lat_long,
                    bat_vol,
                    ignition,
                    dig_ign,
                    movement,
                    eye_bea_mov_1,
                    engine_temp,
                    fuel_level,
                    fuel_level_analog,
                    engine_rpm,
                    odometer_kms,
                    timestamp,
                    ignition_on_counter,
                    gsm_operator,
                    incoming_time
                } = doc || {};

                const mv = (movement && Number(movement) && !!Number(movement)) || (eye_bea_mov_1 && Number(eye_bea_mov_1) && Number(eye_bea_mov_1) === 1)
                const ign = (dig_ign && Number(dig_ign) && !!Number(dig_ign)) || (ignition && Number(ignition) && !!Number(ignition))

                // function secondsToHms(seconds) {
                //     const duration = moment.duration(seconds, 'seconds');
                //     const hh = String(Math.floor(duration.asHours())).padStart(2, '0');
                //     const mm = String(duration.minutes()).padStart(2, '0');
                //     const ss = String(duration.seconds()).padStart(2, '0');

                //     return `${hh}:${mm}:${ss}`;
                // }

                function secondsToHours(seconds) {
                    return (seconds / 3600).toFixed(2);
                }

                const intimeDifference = moment(new Date().getTime()).diff(moment(incoming_time), 'seconds');
                const isDeviceOffline = !(intimeDifference && intimeDifference <= 300);

                const timeDifference = moment(new Date().getTime()).diff(moment(timestamp), 'seconds');
                const isDeviceTimeGap = !(timeDifference && timeDifference <= 300);

                let isSynching = false;
                if (isDeviceTimeGap && incoming_time) {
                    const tts = moment(new Date().getTime()).diff(moment(incoming_time), 'seconds');
                    isSynching = (tts && tts <= 300);
                }

                processedData[key] = {
                    lat_long,
                    timestamp,
                    speed: speed_oem ? speed_oem : speed_gnss ? speed_gnss : '-',
                    speed_title: speed_oem ? 'OEM Speed' : speed_gnss ? 'GPS Speed' : 'Speed',
                    bat_vol: bat_vol && Number(bat_vol) && `${(Number(bat_vol) / 1000).toFixed(2)} Voltage` || '-',
                    status: isSynching ? 'syncing' : isDeviceOffline ? 'offline' : (ign && mv) ? 'moving' : (ign && !mv) ? 'waiting' : 'stop',
                    fuel_level: fuel_level && Number(fuel_level) && `${(Number(fuel_level) / 10).toFixed(2)} ltr(s)` || '-',
                    fuel_level_analog: fuel_level_analog && Number(fuel_level_analog) || 0,
                    engine_temp: engine_temp && Number(engine_temp) && `${(Number(engine_temp)).toFixed(1)} °C` || '-',
                    engine_rpm: engine_rpm && Number(engine_rpm) && `${(Number(engine_rpm)).toFixed(0)} RPM` || '-',
                    meter: odometer_kms && Number(odometer_kms) && `${(Number(odometer_kms) / 1000).toFixed(0)}` || '-',
                    diag_trouble_code: diag_trouble_code && Number(diag_trouble_code) || 0,
                    ignition_on_counter: ignition_on_counter && Number(ignition_on_counter) && secondsToHours(Number(ignition_on_counter)) || 0,
                    gsm_operator
                };
            })
            // console.log('processedData=>', processedData);
            setProcessedDeviceData(processedData);
        } else {
            setProcessedDeviceData({})
        }

    }, [deviceData]);


    useEffect(() => {
        if (props.getTaggedDeviceAssets)
            props.getTaggedDeviceAssets(props.assets && props.assets.length ? props.assets.map(ast => ast._id) : [])
    }, [props.getTaggedDeviceAssets, props.assets]);

    useEffect(() => {

        if (props.taggedDevices && props.taggedDevices.length) {
            // console.log('taggedDevices=>', props.taggedDevices);
            const deviceMap = new Map();
            props.taggedDevices.forEach(d => {
                if (d.parent && d.parent._id && d.tagDeviceId) { // if parent not created and if even tag device dont have device id associated
                    const devAsst = (props.assets || []).filter(ast => ast._id === d.parent._id);
                    if (devAsst.length && d.tagDeviceId)
                        deviceMap.set(`${d.tagDeviceId}-${devAsst[0]._id}`, devAsst[0])
                }
            })
            // console.log('deviceMap=>', deviceMap);
            setDeviceMaps(deviceMap)
        } else {
            setDeviceMaps(new Map())
        }

    }, [props.taggedDevices, props.assets]);

    // Bulk path for last data: same state and counters as the per-device loop,
    // but one request per chunk of devices and one state update per chunk.
    const loadLastDataInBulk = () => {
        const missing = []
        let existingLastDataValues = 0
        deviceMaps.forEach((ast, dev) => {
            if (!assetDeviceLastData[dev]) {
                const [deviceId] = dev.split('-')
                missing.push({ key: dev, device: deviceId, asset: ast._id })
            } else {
                existingLastDataValues++
            }
        })

        const chunks = []
        for (let i = 0; i < missing.length; i += BULK_LAST_DATA_CHUNK_SIZE) {
            chunks.push(missing.slice(i, i + BULK_LAST_DATA_CHUNK_SIZE))
        }

        setPendingRequests(chunks.length)
        setAssetDeviceLastDataLoading(chunks.length > 0)
        setFinishedCallingLastData(existingLastDataValues)

        let cancelled = false

        const runChunk = async (chunk) => {
            const items = chunk.map(({ device, asset }) => ({ device, asset }))
            let result = null
            // One retry after 1.5 s
            for (let attempt = 0; attempt < 2 && !result && !cancelled; attempt++) {
                try {
                    result = await fetchAssetDeviceLastDataBulk(items)
                } catch (error) {
                    console.log('bulk last data error =>', error)
                    if (attempt === 0) await new Promise(resolve => setTimeout(resolve, 1500))
                }
            }
            if (cancelled) return
            if (result && result.data) {
                // Store every key of the chunk, like the per-device loop stores each successful response.
                // No data -> '' (not null): the per-device API returns null as an empty body, so the old
                // loop stored '', and the code reading deviceData (e.g. doc['lat_long'], destructuring)
                // relies on that and crashes on null.
                const merged = {}
                chunk.forEach(({ key, device, asset }) => {
                    const value = result.data[`${device}-${asset}`]
                    merged[key] = value || ''
                })
                setAssetDeviceLastData(prev => ({ ...prev, ...merged }))
            }
            setPendingRequests(prev => Math.max(0, prev - 1))
        }

        const queue = [...chunks]
        const worker = async () => {
            while (queue.length && !cancelled) {
                await runChunk(queue.shift())
            }
        }
        Promise.all(Array.from({ length: Math.min(BULK_LAST_DATA_PARALLEL_CHUNKS, chunks.length) }, worker))

        // Ignore late results when the device list changes or the tab unmounts
        return () => { cancelled = true }
    }

    useEffect(() => {
        // console.log('deviceMaps=> changed ', deviceMaps);
        if (deviceMaps && deviceMaps.size) {
            if (USE_BULK_LAST_DATA) {
                return loadLastDataInBulk()
            }
            // const existingLastData = props.assetDeviceLastData && Object.keys(props.assetDeviceLastData).length ? Object.keys(props.assetDeviceLastData) : [];
            // console.log('existingLastData=>', existingLastData);
            let newPendingRequests = 0;
            let existingLastDataValues = 0;

            deviceMaps.forEach((ast, dev) => {
                // console.log('ast=>', ast);
                // console.log('dev=>', dev);
                if (!assetDeviceLastData[dev]) {
                    const [deviceId, _] = dev.split('-');
                    // props.getAssetDeviceLastData(ast._id, dev);
                    // await axios.get(`/iot-device-data/${device}/${assetId}`);
                    newPendingRequests++;
                    axios.get(`/iot-device-data/${deviceId}/${ast._id}`).then(response => {
                        // console.log('response =>', response.data);
                        setAssetDeviceLastData(prev => ({ ...prev, [dev]: response.data }));
                    }).catch(error => {
                        console.log('last dataerror =>', error);
                    }).finally(() => {
                        setPendingRequests(prev => Math.max(0, prev - 1));
                    })

                } else {
                    // console.log('assetDeviceLastDataExists =>', props.assetDeviceLastData[dev]);
                    existingLastDataValues++;
                }
                // connectToDeviceSSE(keys)
            });

            setPendingRequests(newPendingRequests);
            setAssetDeviceLastDataLoading(newPendingRequests > 0);
            setFinishedCallingLastData(existingLastDataValues);
        } else {
            // console.log('deviceMaps=> no data');
            setDeviceData({})
            setPendingRequests(0);
            setAssetDeviceLastDataLoading(false);
        }
    }, [deviceMaps]);


    useEffect(() => {
        // console.log('assetDeviceLastData=>', assetDeviceLastData && Object.keys(assetDeviceLastData).length);
        // console.log('finishedCallingLastData=>', finishedCallingLastData, deviceMaps.size);
        // Only set as finished when ALL devices have data AND all API requests have completed
        const isAllDataLoaded = assetDeviceLastData && Object.keys(assetDeviceLastData).length === deviceMaps.size;
        const isAllRequestsCompleted = pendingRequests === 0;
        if (props.isLastDataProcessFinished)
            props.isLastDataProcessFinished(isAllDataLoaded && isAllRequestsCompleted)
    }, [assetDeviceLastData, deviceMaps, pendingRequests])

    useEffect(() => {
        // Keep only the rows that exist in the current filtered device map.
        const filteredDeviceData = {}
        const allowedDeviceKeys = new Set(Array.from(deviceMaps.keys()))
        Object.entries(assetDeviceLastData || {}).forEach(([dev, currentDeviceData]) => {
            if (allowedDeviceKeys.has(dev)) {
                filteredDeviceData[dev] = currentDeviceData
            }
        })
        setDeviceData(filteredDeviceData)
    }, [assetDeviceLastData, deviceMaps]);

    useEffect(() => {
        if (map && processedDeviceData) {

            setDevicesMarkerPoint(Object.entries(processedDeviceData).map(([dId, dData], index) => {
                // console.log('dId=>', dId, dData);
                const markerIcon = new Icon({
                    iconUrl: require(`assets/images/icon/${dData.status}.png`),
                    iconSize: [25, 25]
                });
                const [deviceId, parentAssetId] = dId.split('-');
                return {
                    position: [dData.lat_long && dData.lat_long.split(',')[0] || 0, dData.lat_long && dData.lat_long.split(',')[1] || 0],
                    icon: markerIcon,
                    popupContent: null,
                    dData: dData, // Assuming the device data is in 'doc'
                    dId: dId, // Pass the device id
                    deviceMaps: deviceMaps, // Pass the deviceMaps if needed
                    tooltip: true
                }
            }))
        }
    }, [processedDeviceData, map, deviceMaps])

    useEffect(() => {
        // console.log('devicesMarkerPoint=>', devicesMarkerPoint);
        if (props.setDevicePositionalData) {
            props.setDevicePositionalData(devicesMarkerPoint)
        }
    }, [devicesMarkerPoint])

    {/* <Tooltip direction="bottom" offset={[0, 20]} opacity={1}
                            permanent><span style={{
                                fontSize: 'small',
                                fontWeight: 'bold'
                            }}>{deviceMaps.has(dId) ? deviceMaps.get(dId)['assetCode'] : '-'}</span></Tooltip> */}
    // </>

    return (
        <div
            className={`device-map-tab-wrap${isMaximized ? ' device-map-fullscreen' : ''}`}
            style={{ position: 'relative', width: '100%', height: '100%' }}
            onClick={() => setScrollWheelZoom(true)}
            onMouseLeave={() => setScrollWheelZoom(false)}
        >
            {/* ── Maximize / restore button (full screen for this map only) ── */}
            <button
                className="device-map-maximize-btn"
                onClick={(e) => { e.stopPropagation(); setIsMaximized((v) => !v); }}
                title={isMaximized ? 'Restore map' : 'Maximize map'}
            >
                <AntIcon type={isMaximized ? 'fullscreen-exit' : 'fullscreen'} />
            </button>

            {/* ── Floating POI category toggle panel (only shown when POI data exists) ── */}
            {loadedPoiCategories.length > 0 && (
                <div className="device-map-poi-toggle">
                    <button
                        className="device-map-poi-toggle-btn"
                        onClick={(e) => { e.stopPropagation(); setPoiPanelOpen((v) => !v); }}
                        title="Toggle POI categories"
                    >
                        📍 POI Layers {poiPanelOpen ? '▲' : '▼'}
                    </button>
                    {poiPanelOpen && (
                        <div className="device-map-poi-panel">
                            {loadedPoiCategories.map((cat) => {
                                const hidden = hiddenPoiCategories.includes(cat);
                                return (
                                    <AntTooltip key={cat} title={CATEGORY_LABELS[cat]} placement="left">
                                        <button
                                            className={`device-map-poi-cat-btn${hidden ? ' device-map-poi-cat-btn-off' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); togglePoiCategory(cat); }}
                                            style={{ borderLeftColor: hidden ? '#d9d9d9' : CATEGORY_COLORS[cat] }}
                                        >
                                            <span className="device-map-poi-cat-icon">{CATEGORY_ICONS[cat]}</span>
                                            <span className="device-map-poi-cat-label">{CATEGORY_LABELS[cat]}</span>
                                            {hidden
                                                ? <span className="device-map-poi-cat-eye">👁‍🗨</span>
                                                : <span className="device-map-poi-cat-eye" style={{ color: '#52c41a' }}>👁</span>
                                            }
                                        </button>
                                    </AntTooltip>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── Floating tile layer switcher (default / satellite) ── */}
            <div className="device-map-tile-switcher">
                {Object.keys(TILE_LAYERS).map((key) => (
                    <button
                        key={key}
                        className={`device-map-tile-btn${tileLayerType === key ? ' device-map-tile-btn-active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setTileLayerType(key); }}
                        title={TILE_LAYERS[key].label}
                    >
                        {TILE_LAYERS[key].label}
                    </button>
                ))}
            </div>

            <BaseMap
                ref={mapRef}
                center={currentLocation || [20.5937, 78.9629]}
                zoom={5}
                onZoomEnd={handleZoomChange}
                style={{ width: '100%', height: '100%' }}
                showIndiaGeoJson={true}
                showMaaGeoJson={false}
                showGeoFenceOverlay={true}
                geoFenceOverlayOrganisationId={organisationId}
                geoFenceOverlayInteractive={false}
                geoFenceOverlayLabelMinZoom={12}
                geoFenceOverlayHiddenCategories={hiddenPoiCategories}
                geoFenceOverlayOnCategoriesLoaded={setLoadedPoiCategories}
                tileLayerUrl={resolvedTileUrl}
                tileLayerMaxZoom={TILE_LAYERS[tileLayerType].maxZoom || 22}
                tileLayerSubdomains={resolvedTileSubdomains}
                tileLayerAttribution={getTileLayerAttribution(tileLayerType)}
                geoJsonLabelsZoom={currentZoom}
                geoJsonLabelsMinZoom={17}
                scrollWheelZoom={scrollWheelZoom}
            >
                {/* Hybrid: street label overlay on top of satellite */}
                {TILE_LAYERS[tileLayerType] && TILE_LAYERS[tileLayerType].overlay && (
                    <TileLayer
                        url={TILE_LAYERS[tileLayerType].overlay}
                        subdomains={TILE_LAYERS[tileLayerType].overlaySubdomains || ['a', 'b', 'c']}
                        opacity={TILE_LAYERS[tileLayerType].overlayOpacity || 0.4}
                        maxZoom={22}
                        attribution={TILE_LAYERS[tileLayerType].overlayAttribution}
                    />
                )}
                <MapGroup mapRef={mapRef} markers={devicesMarkerPoint} />
            </BaseMap>
        </div>
    );
}

export default DeviceMap;
